import { safeParseInt } from "encamp-shared/src/utils/numberParsing/safeParseNumber";

/**
 * Triggers a browser download from a URL without opening a tab or navigating
 * away from the app.
 *
 * Signed S3 URLs are signed with `Content-Disposition: attachment` (see
 * `getSignedUrl` in encamp-shared), so clicking a link to one downloads the file
 * and leaves the current page where it is. That is what makes a plain anchor the
 * right primitive here:
 *
 * - `window.open` needs a user gesture to survive Safari's popup blocker, and
 *   every download in the app resolves a signed URL from the server first, so
 *   the call always lands a tick too late to count as one.
 * - Assigning `window.location` can only be done once per click, which would
 *   silently drop every file but one when a caller downloads several.
 * - A hidden iframe would sidestep navigation entirely, but the frontend CSP's
 *   `frame-src` allowlist has no S3 origin in it, deliberately.
 *
 * The download is named by the `Content-Disposition` the URL was signed with —
 * browsers ignore the anchor `download` attribute cross-origin, so a name the
 * file should land under has to be signed into the URL server-side
 * (`getSignedDownloadUrl`), not passed here.
 */
export const downloadFromUrl = (url: string): void => {
  const link = document.createElement("a");
  link.href = url;
  link.style.display = "none";
  document.body.appendChild(link);
  link.click();
  document.body.removeChild(link);
};

const AMZ_DATE_PATTERN = /^(\d{4})(\d{2})(\d{2})T(\d{2})(\d{2})(\d{2})Z$/;

/**
 * Whether a presigned S3 URL's signature window has already closed, judged
 * locally from its own `X-Amz-Date` + `X-Amz-Expires` query params — no
 * request needed. Signed URLs stored in client state (e.g. minted when a chat
 * history loaded) can outlive their window, and a same-tab anchor click on an
 * expired one navigates the SPA to S3's 403 XML instead of downloading, so
 * callers check this first and re-sign or explain instead. Returns false when
 * the params are absent or unparsable — an unknown age is no proof of expiry.
 */
export const isPresignedUrlExpired = (url: string, nowMs = Date.now()): boolean => {
  let params: URLSearchParams;
  try {
    params = new URL(url).searchParams;
  } catch {
    return false;
  }
  const match = params.get("X-Amz-Date")?.match(AMZ_DATE_PATTERN);
  const expiresSeconds = safeParseInt(params.get("X-Amz-Expires"));
  if (!match || expiresSeconds === null) {
    return false;
  }
  const [, year, month, day, hour, minute, second] = match;
  const signedAtMs = Date.parse(`${year}-${month}-${day}T${hour}:${minute}:${second}Z`);
  return nowMs >= signedAtMs + expiresSeconds * 1000;
};

/**
 * Opens a URL that only becomes known after an async step (signing, a query)
 * in a new tab, without losing the user gesture.
 *
 * `window.open(url)` after an `await` runs outside the click's gesture window,
 * where Safari's popup blocker eats it. The tab has to be claimed
 * synchronously inside the handler — blank — and pointed at the URL once it
 * resolves. Only useful for URLs signed `inline`; an attachment-signed URL
 * downloads from the blank tab and leaves it stranded.
 */
export const openInNewTabFromPromise = async (
  getUrl: () => Promise<string | undefined | null>
): Promise<void> => {
  const tab = window.open("", "_blank");
  if (tab) {
    // Sever the reverse handle before untrusted content loads in the tab.
    tab.opener = null;
  }
  let url: string | undefined | null;
  try {
    url = await getUrl();
  } catch (error) {
    tab?.close();
    throw error;
  }
  if (!url) {
    tab?.close();
    return;
  }
  if (tab) {
    // `replace` keeps the blank page out of the tab's history, so Back
    // doesn't land on about:blank.
    tab.location.replace(url);
  } else {
    // The popup was blocked even inside the gesture (aggressive settings).
    // Callers hand this inline-signed URLs, so a plain anchor click would be
    // a same-tab navigation that renders the document over the SPA — click a
    // _blank anchor instead: best case the blocker allows it, worst case
    // nothing happens, and the current tab is never lost either way.
    const link = document.createElement("a");
    link.href = url;
    link.target = "_blank";
    link.rel = "noopener";
    link.style.display = "none";
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
  }
};

/**
 * How long to wait between anchor clicks when downloading several files.
 *
 * An anchor click is a provisional top-level navigation until the response's
 * `Content-Disposition: attachment` headers arrive and convert it into a
 * download; a second click before that cancels the first navigation, so a
 * tight loop of clicks delivers only the last file. One second comfortably
 * covers S3's time-to-first-byte while keeping a multi-file export quick.
 */
export const MULTI_DOWNLOAD_CLICK_SPACING_MS = 1000;

const sleep = (ms: number) => new Promise((resolve) => setTimeout(resolve, ms));

/**
 * Downloads several files in a row, spacing the anchor clicks out so each
 * navigation has converted into a download before the next click can cancel
 * it. Resolves after the last click; the downloads themselves keep streaming
 * in the browser's download manager.
 */
export const downloadFromUrls = async (urls: string[]): Promise<void> => {
  for (const [index, url] of urls.entries()) {
    if (index > 0) {
      await sleep(MULTI_DOWNLOAD_CLICK_SPACING_MS);
    }
    downloadFromUrl(url);
  }
};
